import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';
import SoundManager from '../utils/SoundManager';

// "Dive" — the visitor falls through the layers of the net.
// Vertical cables of light, pulsing nodes by depth band, scrolling
// fragments of code, and occasional Ghost-in-the-Shell quotes in the HUD.

const QUOTES = [
  'Your effort to remain what you are is what limits you.',
  'And yet you still cling to the notion that you are a person.',
  'What we call "us" is the sum of our experiences.',
  'Net is vast and infinite.',
  'A copy is just an identical impression of the original.',
  'No matter how powerful we may be, we should not be able to escape from our self-doubt.',
  'I refer to myself as an intelligent life form because I am sentient.',
  'Sometimes I wonder if I am a ghost in a borrowed shell.',
  'The data isn\'t hiding. It is just very, very far down.',
];

const CODE_FRAGMENTS = [
  '0x4A3F2C', '0xFFE001', 'init_ghost()', 'kernel_panic', 'TCP/9001',
  'ssh -i ghost', 'mov eax, ebx', 'segfault', '0xDEADBEEF',
  'route::deeper', '404: soul', 'auth=null', 'shell::recv',
  'pkt_drop', 'ICMP', 'sig::SIGGHOST', 'ptr_void',
];

const KANA = '00111010アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';

const DiveVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentQuote, setCurrentQuote] = useState<string>('');
  const [depthLabel, setDepthLabel] = useState<string>('SURFACE');

  useEffect(() => {
    if (!containerRef.current) return;
    SoundManager.playAmbient();

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.Fog(0x000000, 5, 40);

    const camera = new THREE.PerspectiveCamera(80, width / height, 0.1, 200);
    camera.position.set(0, 0, 0);
    camera.lookAt(0, -1, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    // === Vertical cables of light ===
    // We create N tall thin boxes scattered radially, then move them up
    // continuously to simulate downward camera motion.
    const CABLE_COUNT = 80;
    type Cable = { mesh: THREE.Mesh; speed: number; baseHue: number };
    const cables: Cable[] = [];
    for (let i = 0; i < CABLE_COUNT; i++) {
      const radius = 4 + Math.random() * 18;
      const angle = Math.random() * Math.PI * 2;
      const height = 8 + Math.random() * 24;
      const hue = Math.random() < 0.7 ? 130 : 180 + Math.random() * 50; // mostly green, some cyan
      const mat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(`hsl(${hue}, 90%, 60%)`),
        transparent: true,
        opacity: 0.4 + Math.random() * 0.5,
      });
      const geo = new THREE.BoxGeometry(0.04 + Math.random() * 0.08, height, 0.04 + Math.random() * 0.08);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(Math.cos(angle) * radius, -30 + Math.random() * 60, Math.sin(angle) * radius);
      scene.add(mesh);
      cables.push({ mesh, speed: 0.15 + Math.random() * 0.5, baseHue: hue });
    }

    // === Pulsing nodes (small spheres at intervals) ===
    const NODE_COUNT = 40;
    type Node = { mesh: THREE.Mesh; baseSize: number; phase: number };
    const nodes: Node[] = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      const radius = 2 + Math.random() * 12;
      const angle = Math.random() * Math.PI * 2;
      const size = 0.05 + Math.random() * 0.12;
      const mat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(0x7aff94),
        transparent: true,
        opacity: 0.8,
      });
      const geo = new THREE.SphereGeometry(size, 8, 8);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(Math.cos(angle) * radius, -30 + Math.random() * 60, Math.sin(angle) * radius);
      scene.add(mesh);
      nodes.push({ mesh, baseSize: size, phase: Math.random() * Math.PI * 2 });
    }

    // === Floating text sprites (code fragments / kana) ===
    const makeTextSprite = (text: string, hue: number): THREE.Sprite => {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 64;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.fillRect(0, 0, 256, 64);
      ctx.font = "bold 28px 'C64', 'Courier New', monospace";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = `hsl(${hue}, 90%, 60%)`;
      ctx.shadowBlur = 12;
      ctx.fillStyle = `hsl(${hue}, 90%, 70%)`;
      ctx.fillText(text, 128, 32);
      const tex = new THREE.CanvasTexture(canvas);
      tex.needsUpdate = true;
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
      const s = new THREE.Sprite(mat);
      s.scale.set(2.5, 0.6, 1);
      return s;
    };

    type FloatingText = { sprite: THREE.Sprite; speed: number };
    const texts: FloatingText[] = [];
    const spawnText = (yOverride?: number) => {
      const isCode = Math.random() < 0.5;
      const content = isCode
        ? CODE_FRAGMENTS[Math.floor(Math.random() * CODE_FRAGMENTS.length)]
        : Array.from({ length: 6 }, () => KANA[Math.floor(Math.random() * KANA.length)]).join('');
      const hue = Math.random() < 0.7 ? 130 : 50; // green or amber
      const sprite = makeTextSprite(content, hue);
      const radius = 3 + Math.random() * 10;
      const angle = Math.random() * Math.PI * 2;
      sprite.position.set(Math.cos(angle) * radius, yOverride ?? -25, Math.sin(angle) * radius);
      scene.add(sprite);
      texts.push({ sprite, speed: 0.2 + Math.random() * 0.4 });
    };
    for (let i = 0; i < 20; i++) spawnText(-30 + i * 3);

    // === Camera motion ===
    let lookY = -1;

    // === HUD: rotate quotes & depth labels ===
    const depthBands = ['SURFACE', 'BACKBONE', 'L3 ROUTING', 'CORE', 'DEEP CORE', 'GHOST STRATA', '???'];
    let depthIdx = 0;
    setDepthLabel(depthBands[depthIdx]);
    const depthInterval = window.setInterval(() => {
      depthIdx = (depthIdx + 1) % depthBands.length;
      setDepthLabel(depthBands[depthIdx]);
    }, 7000);

    let quoteIdx = Math.floor(Math.random() * QUOTES.length);
    setCurrentQuote(QUOTES[quoteIdx]);
    const quoteInterval = window.setInterval(() => {
      quoteIdx = (quoteIdx + 1) % QUOTES.length;
      setCurrentQuote(QUOTES[quoteIdx]);
    }, 9000);

    const onResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    const start = performance.now();
    let frameId = 0;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const t = (performance.now() - start) / 1000;

      // Move cables UP (camera falls DOWN). Wrap when they go past the top.
      for (const c of cables) {
        c.mesh.position.y += c.speed * 0.1;
        if (c.mesh.position.y > 25) c.mesh.position.y = -25;
      }

      // Pulse nodes
      for (const n of nodes) {
        n.mesh.position.y += 0.04;
        if (n.mesh.position.y > 25) n.mesh.position.y = -25;
        const pulse = 1 + Math.sin(t * 2 + n.phase) * 0.4;
        n.mesh.scale.setScalar(pulse);
        (n.mesh.material as THREE.MeshBasicMaterial).opacity = 0.5 + Math.sin(t * 2 + n.phase) * 0.3;
      }

      // Rise text sprites
      for (let i = texts.length - 1; i >= 0; i--) {
        const ft = texts[i];
        ft.sprite.position.y += ft.speed * 0.12;
        if (ft.sprite.position.y > 25) {
          scene.remove(ft.sprite);
          ft.sprite.material.map?.dispose();
          ft.sprite.material.dispose();
          texts.splice(i, 1);
        }
      }
      if (texts.length < 25 && Math.random() < 0.04) spawnText(-25);

      // Subtle camera sway
      camera.position.x = Math.sin(t * 0.3) * 0.4;
      camera.position.z = Math.cos(t * 0.25) * 0.4;
      lookY = -1 + Math.sin(t * 0.5) * 0.1;
      camera.lookAt(0, lookY, 0);

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
      window.clearInterval(depthInterval);
      window.clearInterval(quoteInterval);
      // dispose floating text textures
      for (const ft of texts) {
        ft.sprite.material.map?.dispose();
        ft.sprite.material.dispose();
      }
      disposeScene(scene, renderer);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="relative w-full h-full bg-black">
      <div ref={containerRef} className="w-full h-full" />
      {/* HUD top-left: depth label */}
      <div className="absolute top-3 left-3 text-[#7aff94] pointer-events-none font-mono">
        <div
          className="text-lg font-bold"
          style={{ textShadow: '0 0 8px rgba(122,255,148,0.8)' }}
        >
          DIVE
        </div>
        <div className="text-xs opacity-80">
          DEPTH: <span className="text-[#ffd166]">{depthLabel}</span>
        </div>
        <div className="text-xs opacity-50">Net is vast and infinite.</div>
      </div>
      {/* HUD bottom: rotating quote */}
      <div
        className="absolute bottom-4 left-4 right-4 text-[#5fe979] font-mono text-sm pointer-events-none"
        style={{ textShadow: '0 0 6px rgba(95,233,121,0.6)' }}
      >
        <div className="opacity-70">{'>'} GHOST-LINE WHISPER:</div>
        <div
          key={currentQuote}
          className="opacity-90 italic"
          style={{ animation: 'fadeIn 1s ease-in' }}
        >
          "{currentQuote}"
        </div>
      </div>
      {/* HUD top-right: vitals */}
      <div className="absolute top-3 right-3 text-[#5fe979] text-xs font-mono pointer-events-none opacity-70 text-right">
        <div>SIGNAL: <span className="text-[#7aff94]">LOCKED</span></div>
        <div>GHOST-INTEGRITY: 100%</div>
        <div>EXIT: ESC</div>
      </div>
    </div>
  );
};

export default DiveVisualization;
